// Initialize email configuration
const BREVO_API_KEY = import.meta.env.VITE_BREVO_API_KEY || '';
const BREVO_API_URL = 'https://api.brevo.com/v3/smtp/email';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export async function send2FACode(
  toEmail: string,
  verificationCode: string
): Promise<boolean> {
  if (!BREVO_API_KEY) {
    console.warn('Brevo API key not configured');
    return false;
  }

  let retries = 0;
  try {
    while (retries < MAX_RETRIES) {
      try {
        const response = await fetch(BREVO_API_URL, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'api-key': BREVO_API_KEY
          },
          body: JSON.stringify({
            sender: {
              name: 'PM Pro AI',
              email: 'hello@pmproai.com'
            },
            to: [{
              email: toEmail
            }],
            subject: 'Your Two-Factor Authentication Code',
            htmlContent: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #4F46E5;">Your Verification Code</h2>
                <p style="color: #374151; font-size: 16px; line-height: 24px;">
                  Here is your verification code for PM Pro AI. This code will expire in 5 minutes:
                </p>
                <div style="margin: 32px 0; text-align: center;">
                  <div style="background-color: #F3F4F6; padding: 16px; border-radius: 8px; font-size: 24px; letter-spacing: 4px; font-family: monospace;">
                    ${verificationCode}
                  </div>
                </div>
                <p style="color: #6B7280; font-size: 14px;">
                  This code will expire in 5 minutes. If you didn't request this code, please ignore this email.
                </p>
                <hr style="border: none; border-top: 1px solid #E5E7EB; margin: 32px 0;">
                <p style="color: #9CA3AF; font-size: 12px;">
                  This is an automated message from PM Pro AI. Please do not reply to this email.
                </p>
              </div>
            `
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return true;
      } catch (error) {
        retries++;
        if (retries === MAX_RETRIES) throw error;
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * retries));
      }
    }
    throw new Error('Max retries reached');
  } catch (error) {
    console.error('Error sending 2FA email:', error);
    return false;
  }
}

export async function sendPasswordResetEmail(
  toEmail: string,
  resetToken: string
): Promise<boolean> {
  try {
    if (!BREVO_API_KEY || BREVO_API_KEY.trim() === '') {
      console.error('Brevo API key not configured');
      return false;
    }

    // Construct the actual reset link with the token
    const resetLink = `${window.location.origin}/auth?type=recovery&token=${resetToken}`;

    let retries = 0;
    while (retries < MAX_RETRIES) {
      try {
        const response = await fetch(BREVO_API_URL, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'api-key': BREVO_API_KEY
          },
          body: JSON.stringify({
            sender: {
              name: 'PM Pro AI',
              email: 'hello@pmproai.com'
            },
            to: [{
              email: toEmail
            }],
            subject: 'Reset Your PM Pro AI Password',
            htmlContent: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #4F46E5;">Reset Your Password</h2>
                <p style="color: #374151; font-size: 16px; line-height: 24px;">
                  We received a request to reset your PM Pro AI password. Click the button below to choose a new password:
                </p>
                <div style="margin: 32px 0; text-align: center;">
                  <a href="${resetLink}" style="display: inline-block; background-color: #4F46E5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500;">
                    Reset Password
                  </a>
                </div>
                <p style="color: #6B7280; font-size: 14px;">
                  This link will expire in 24 hours. If you didn't request a password reset, you can safely ignore this email.
                </p>
                <hr style="border: none; border-top: 1px solid #E5E7EB; margin: 32px 0;">
                <p style="color: #9CA3AF; font-size: 12px;">
                  This is an automated message from PM Pro AI. Please do not reply to this email.
                </p>
              </div>
            `
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return true;
      } catch (error) {
        retries++;
        if (retries === MAX_RETRIES) throw error;
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * retries));
      }
    }
    throw new Error('Max retries reached');
  } catch (error) {
    console.error('Error sending password reset email:', error instanceof Error ? error.message : error);
    return false;
  }
}